import {
  validateAnalyticsEvent,
  type AnalyticsEventName,
  type AnalyticsEventProperties,
  type AnalyticsEventPropertiesMap,
} from '@/app/lib/analytics/eventContract.generated';
import {
  getPostHogClient,
  identifyWithPostHog,
  resetPostHogIdentity,
} from '@/app/lib/posthogClient';

export type { AnalyticsEventName, AnalyticsEventProperties, AnalyticsEventPropertiesMap };

export const identifyUser = (userId: string) => {
  identifyWithPostHog(userId);
};

/**
 * The single custom-event boundary. Invalid data is dropped so analytics can
 * never break the product action it observes.
 */
export function trackEvent<N extends AnalyticsEventName>(
  eventName: N,
  properties: AnalyticsEventProperties<N>,
): boolean {
  const validation = validateAnalyticsEvent(eventName, properties);
  if (!validation.ok) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`[analytics] Dropped ${eventName}: ${validation.reason}`);
    }
    return false;
  }

  const posthog = getPostHogClient();
  if (!posthog?.__loaded) return false;
  posthog.capture(eventName, {
    ...validation.value,
    event_schema_version: 1,
  });
  return true;
}

export const getFeatureFlag = (flagKey: string) => {
  const posthog = getPostHogClient();
  return posthog?.__loaded ? posthog.getFeatureFlag(flagKey) : null;
};

export const isFeatureEnabled = (flagKey: string) => {
  const posthog = getPostHogClient();
  return posthog?.__loaded ? posthog.isFeatureEnabled(flagKey) : false;
};

export const resetUser = () => {
  resetPostHogIdentity();
};

export const trackScheduleGenerated = (
  properties: AnalyticsEventPropertiesMap['schedule_generated'],
) => trackEvent('schedule_generated', properties);

export const trackCourseAdded = () =>
  trackEvent('course_added', { source: 'schedule_builder' });

export const trackCourseRemoved = () =>
  trackEvent('course_removed', { source: 'schedule_builder' });

export const trackScheduleSaved = (
  courseCount: number,
  saveSource: AnalyticsEventPropertiesMap['schedule:saved']['save_source'],
) => trackEvent('schedule:saved', {
  course_count: courseCount,
  save_source: saveSource,
});

export const trackUserLogin = (
  method: AnalyticsEventPropertiesMap['user_logged_in']['method'],
) => trackEvent('user_logged_in', { method });
